// dashboard/src/pages/StaffDasboard/fieldOfficerAssetsPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Package, RefreshCw, Clipboard, Info } from "lucide-react";
import MasterSideBar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import MyAssetsTable from "../../components/operationsAssociate/MyAssetsTable";
import ReturnAssetModal from "../../components/operationsAssociate/ReturnAssetModal";
import ViewAssetModal from "../../components/operationsAssociate/ViewAssetModal";

const API_URL = "https://ims.holdthechild.net";

const FieldOfficerAssetsPage = () => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [loggedInStaffId, setLoggedInStaffId] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Fetch User ID
    useEffect(() => {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                if (user && (user.id || user._id)) {
                    setLoggedInStaffId(user.id || user._id);
                } else {
                    toast.error("User ID not found. Please log in again.");
                }
            } catch (e) {
                console.error("Error parsing user", e);
            }
        } else {
            toast.error("Session expired. Please log in again.");
        }
    }, []);

    // Fetch Assets
    const fetchMyAssets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Auth token missing");
            
            const response = await axios.get(`${API_URL}/api/assets/my-assets`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAssets(response.data.data || []);
        } catch (err) {
            setError("Unable to fetch assets.");
            toast.error("Failed to load your assigned assets.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (loggedInStaffId) fetchMyAssets();
    }, [loggedInStaffId, fetchMyAssets]);

    // --- Handlers ---

    const handleOpenReturnModal = (asset) => {
        setSelectedAsset(asset);
        setIsReturnModalOpen(true);
    };

    const handleCloseReturnModal = () => {
        setIsReturnModalOpen(false);
        setSelectedAsset(null);
        fetchMyAssets(); // Refresh table after return
    };

    const handleOpenViewModal = (asset) => {
        setSelectedAsset(asset);
        setIsViewModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedAsset(null);
        fetchMyAssets(); // Optional: Refresh table in case details changed
    };

    return (
        <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
            <MasterSideBar
                visible={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                setCollapsed={setSidebarCollapsed}
                onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
            />

            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
                <TopBar onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={() => {}} />

                <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
                    <div className="max-w-6xl mx-auto">
                        
                        {/* Header Section */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                                    Assigned <span className="text-orange-600">Assets</span>
                                </h1>
                                <p className="text-gray-500 text-sm italic">Track and manage inventory currently in your possession.</p>
                            </div>
                            <button 
                                onClick={fetchMyAssets}
                                className="p-3 bg-white border border-gray-100 rounded-2xl text-gray-400 hover:text-orange-600 hover:shadow-md transition-all"
                            >
                                <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
                            </button>
                        </div>

                        {/* Inventory Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-orange-100">
                                        <Package size={20} />
                                    </div>
                                    <span className="text-2xl font-black text-gray-800">{assets.length}</span>
                                </div>
                                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Assets</h3>
                            </div>

                            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-100">
                                        <Clipboard size={20} />
                                    </div>
                                    <span className="text-2xl font-black text-gray-800">
                                        {/* UPDATED: Fixed status check to match 'Active' instead of 'active' */}
                                        {assets.filter(a => a.status === 'Active' || a.status === 'In Use').length}
                                    </span>
                                </div>
                                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Operational</h3>
                            </div>

                            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm group hover:border-orange-200 transition-all cursor-help">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 group-hover:bg-orange-50 group-hover:text-orange-600 transition-all">
                                        <Info size={20} />
                                    </div>
                                </div>
                                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Return Policy</h3>
                                <p className="text-[9px] text-gray-400 mt-1 uppercase font-bold">Follow standard return procedures</p>
                            </div>
                        </div>

                        {/* Assets Table Container */}
                        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-6 border-b border-gray-50 bg-gray-50/30">
                                <h2 className="text-xs font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                                    Active Inventory List
                                </h2>
                            </div>

                            <div className="p-2">
                                {loading ? (
                                    <div className="py-20 text-center">
                                        <RefreshCw className="w-8 h-8 text-orange-600 animate-spin mx-auto mb-4" />
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Synchronizing Inventory...</p>
                                    </div>
                                ) : (
                                    <MyAssetsTable
                                        assets={assets}
                                        error={error}
                                        onReturn={handleOpenReturnModal}
                                        onView={handleOpenViewModal}
                                        loggedInStaffId={loggedInStaffId}
                                        // CRITICAL: Added refreshAssets so Accept/Reject buttons work
                                        refreshAssets={fetchMyAssets}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Modals */}
            {isReturnModalOpen && selectedAsset && (
                <ReturnAssetModal
                    asset={selectedAsset}
                    closeModal={handleCloseReturnModal}
                />
            )}
            {isViewModalOpen && selectedAsset && (
                <ViewAssetModal
                    asset={selectedAsset}
                    closeModal={handleCloseViewModal}
                />
            )}
        </div>
    );
};

export default FieldOfficerAssetsPage;